<template>
  <div :class="{ editable }" class="editor">
    <template v-if="caseDef.studies.length > 0">
      <StudyList
        class="mb-4"
        :case-def="caseDef"
        selectable
        :selected-series="selectedSeries"
        @select-series="emits('set-selected-series', $event)"
        :editable="editable"
        @toggle-series-removed="toggleSeriesRemoved"
        @toggle-study-removed="toggleStudyRemoved"
      ></StudyList>
      <SeriesViewer
        v-if="selectedSeries"
        :series="selectedSeries"
        :editable="editable"
        @update-redaction-boxes="updateRedactionBoxes"
        @remove-redactions="removeRedactions"
        @toggle-image-removed="toggleImageRemoved"
        @include-all-images="includeAllSeriesImages"
        :removed-instance-uids="caseDef.markedForRemoval?.instanceUIDs"
        :matching-series-count="matchingSeriesCount"
        :key="viewerKey"
        ref="viewer"
      ></SeriesViewer>
      <!-- <button @click="viewerKey++">Reload viewer</button> -->
    </template>
    <div v-else class="text-sm text-base-content/60">No images loaded.</div>
  </div>
</template>

<script setup lang="ts">
import SeriesViewer from './SeriesViewer.vue';
import StudyList from '../StudyList.vue';
import CaseDef from '../types/CaseDef';
import Series from '../types/Series';
import {
  updateRedaction,
  updateRedactionAcross,
  seriesMatchingMatrix,
  removeRedactions as clearSeriesRedactions,
} from '../pixel-redaction';
import { addLog } from '../logger';
import { computed, ref, watch } from 'vue';

const viewerKey = ref(0);
const viewer = ref<InstanceType<typeof SeriesViewer> | null>(null);

const props = defineProps<{ caseDef: CaseDef; selectedSeries: Series | null; editable: boolean }>();

const emits = defineEmits(['set-selected-series']);

/**
 * Commit any boxes the user has drawn but not yet applied, to `series`.
 *
 * Called before the viewer is torn down or the case is finalized, so an open
 * redaction session isn't silently thrown away. Returns true if anything was
 * committed. Safe to call when nothing is pending.
 */
function commitPendingRedactions(series: Series | null = props.selectedSeries): boolean {
  const pending = viewer.value?.takePendingRedactions();
  if (!pending?.length || !series) return false;
  updateRedaction(pending, series);
  addLog('info', `${series.seriesDescription || series.uid}: applied redaction boxes left open in the editor`);
  return true;
}

defineExpose({ commitPendingRedactions });

watch(
  () => props.selectedSeries,
  (_next, previous) => {
    // Pre-flush: the viewer still exists and hasn't re-rendered, but props here
    // already point at the new series — so commit explicitly to the old one.
    commitPendingRedactions(previous ?? null);
    viewerKey.value++;
  }
);

watch(
  () => props.editable,
  () => {
    viewerKey.value++;
  }
);

/** The study holding `series`, since a Series carries no back-reference. */
function studyContaining(series: Series) {
  return props.caseDef.studies.find((study) => study.series.includes(series));
}

/**
 * Other series the current one's rectangles can be applied to verbatim. Offered in
 * the viewer as a bulk apply, which matters most for the split ultrasound studies
 * that made this necessary: a forty-clip echo is forty separate parts to redact,
 * and redacting them one at a time is its own PHI risk.
 */
const matchingSeriesCount = computed(() => {
  const series = props.selectedSeries;
  if (!series) return 0;
  const study = studyContaining(series);
  return study ? seriesMatchingMatrix(study, series).length : 0;
});

// Both of these just move rectangles in and out of the redaction store and drop the
// series' frames from the image cache; the remount re-decodes them through the
// current rectangles. No pixels are copied and nothing is persisted, so undo can't
// fail the way the old restore-from-backup path could.
function updateRedactionBoxes(
  redactionBoxes: any,
  scope: 'series' | 'study' = 'series'
) {
  updateRedaction(redactionBoxes, props.selectedSeries);

  if (scope === 'study' && props.selectedSeries) {
    const study = studyContaining(props.selectedSeries);
    const matching = study
      ? seriesMatchingMatrix(study, props.selectedSeries)
      : [];
    updateRedactionAcross(redactionBoxes, matching);
    addLog(
      'info',
      `Added the same redaction boxes to ${matching.length} other series in this study at the same image size (any redactions those series already had are kept)`
    );
  }

  viewerKey.value++;
}

function removeRedactions() {
  clearSeriesRedactions(props.selectedSeries);
  viewerKey.value++;
}

function ensureMarkedForRemoval() {
  if (!props.caseDef.markedForRemoval) {
    props.caseDef.markedForRemoval = {
      studyUIDs: [],
      seriesUIDs: [],
      instanceUIDs: [],
    };
  }
}

function toggleImageRemoved(instanceUID: string) {
  ensureMarkedForRemoval();
  const uidIndex = props.caseDef.markedForRemoval!.instanceUIDs.indexOf(instanceUID);
  if (uidIndex > -1) {
    props.caseDef.markedForRemoval!.instanceUIDs.splice(uidIndex, 1);
  } else {
    props.caseDef.markedForRemoval!.instanceUIDs.push(instanceUID);
  }
}

/**
 * Undo every one-at-a-time image exclusion in a series, so a mis-click doesn't
 * mean scrolling back through the stack to find and re-include each image.
 * Splices in place: SeriesViewer holds this same array as a prop.
 */
function includeAllSeriesImages() {
  const series = props.selectedSeries;
  if (!series) return;
  ensureMarkedForRemoval();
  const excluded = props.caseDef.markedForRemoval!.instanceUIDs;
  const inSeries = new Set(series.instances.map((instance) => instance.uid));
  const kept = excluded.filter((uid) => !inSeries.has(uid));
  if (kept.length === excluded.length) return;
  excluded.splice(0, excluded.length, ...kept);
}

function toggleStudyRemoved(studyUID: string) {
  ensureMarkedForRemoval();
  const uidIndex = props.caseDef.markedForRemoval!.studyUIDs.indexOf(studyUID);
  if (uidIndex > -1) {
    props.caseDef.markedForRemoval!.studyUIDs.splice(uidIndex, 1);
  } else {
    props.caseDef.markedForRemoval!.studyUIDs.push(studyUID);
  }
}

function toggleSeriesRemoved(seriesUID: string) {
  ensureMarkedForRemoval();
  const uidIndex = props.caseDef.markedForRemoval!.seriesUIDs.indexOf(seriesUID);
  if (uidIndex > -1) {
    props.caseDef.markedForRemoval!.seriesUIDs.splice(uidIndex, 1);
  } else {
    props.caseDef.markedForRemoval!.seriesUIDs.push(seriesUID);
  }
}
</script>

<style scoped>
.editor {
  background-color: var(--color-base-200);
  border: 1px solid var(--color-base-300);
  padding: 1.25rem;
}
.editable {
  background-color: color-mix(in oklab, var(--color-warning) 10%, transparent);
  border-color: var(--color-warning);
}
</style>
